'use client';

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

export type GeolocationPermission =
  | 'prompt'
  | 'granted'
  | 'denied'
  | 'unavailable';

export interface UserLocation {
  lat: number;
  lng: number;
  accuracy: number;
  timestamp: number;
}

export interface UseUserLocationReturn {
  /** Current user location, null if not yet obtained */
  location: UserLocation | null;
  /** Permission state */
  permission: GeolocationPermission;
  /** Whether a geolocation request is in progress */
  isLocating: boolean;
  /** Request the user's location (triggers browser permission prompt if needed) */
  requestLocation: () => Promise<UserLocation | null>;
  /** Clear the cached location */
  clearLocation: () => void;
}

const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 300000, // 5 min cache
};

// ---------------------------------------------------------------------------
// Shared module-level state — all useUserLocation() instances see the same data
// ---------------------------------------------------------------------------

interface LocationStore {
  location: UserLocation | null;
  permission: GeolocationPermission;
  isLocating: boolean;
  dismissed: boolean;
}

let store: LocationStore = {
  location: null,
  permission: 'prompt',
  isLocating: false,
  dismissed: false,
};

const listeners = new Set<() => void>();

const getSnapshot = () => store;
const getServerSnapshot = () => store;

const notify = () => {
  listeners.forEach((listener) => listener());
};

const setStore = (updates: Partial<LocationStore>) => {
  store = { ...store, ...updates };
  notify();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const useUserLocation = (): UseUserLocationReturn => {
  const { location, permission, isLocating, dismissed } =
    useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const permissionCheckedRef = useRef(false);

  // Check initial permission state (once globally)
  useEffect(() => {
    if (permissionCheckedRef.current) return;
    permissionCheckedRef.current = true;

    if (typeof window === 'undefined' || !navigator.geolocation) {
      setStore({ permission: 'unavailable' });
      return;
    }

    if (!navigator.permissions) return;

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((status) => {
        setStore({ permission: status.state as GeolocationPermission });

        status.addEventListener('change', () => {
          setStore({ permission: status.state as GeolocationPermission });
        });
      })
      .catch(() => {
        // Permissions API not supported, stay as 'prompt'
      });
  }, []);

  // Auto-fetch when permission is already granted
  useEffect(() => {
    if (permission === 'granted' && !location && !isLocating && !dismissed) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setStore({
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp,
            },
          });
        },
        () => {
          // Silently fail
        },
        GEOLOCATION_OPTIONS
      );
    }
  }, [permission, location, isLocating, dismissed]);

  const requestLocation = useCallback((): Promise<UserLocation | null> => {
    if (typeof window === 'undefined' || !navigator.geolocation) {
      setStore({ permission: 'unavailable' });
      return Promise.resolve(null);
    }

    setStore({ dismissed: false, isLocating: true });

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation: UserLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          };
          setStore({
            location: userLocation,
            permission: 'granted',
            isLocating: false,
          });
          resolve(userLocation);
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setStore({ permission: 'denied', isLocating: false });
          } else {
            setStore({ isLocating: false });
          }
          resolve(null);
        },
        GEOLOCATION_OPTIONS
      );
    });
  }, []);

  const clearLocation = useCallback(() => {
    setStore({ location: null, dismissed: true });
  }, []);

  return {
    location,
    permission,
    isLocating,
    requestLocation,
    clearLocation,
  };
};

export default useUserLocation;
